import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Download, IndianRupee, CheckCircle, Clock, XCircle, RotateCcw, Search, RefreshCw } from 'lucide-react'
import { Pagination } from '../../common/Select/Select'
import { fetchTransactions } from './services/transactionSlice'

const STATUS = {
  success:  { cls: 'badge-active',  l: 'Paid' },
  pending:  { cls: 'badge-pending', l: 'Pending' },
  failed:   { cls: 'badge-danger',  l: 'Failed' },
  refunded: { cls: 'badge-gold',    l: 'Refunded' },
}

const formatMoney = (n, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 2,
  }).format(Number(n || 0))

const formatDate = (d) => {
  if (!d) return '—'
  const dt = new Date(d)
  if (isNaN(dt)) return '—'
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`

export default function Transaction() {
  const dispatch = useDispatch()
  const { list, summary, isLoading, isError, message } = useSelector((state) => state.transaction)

  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    dispatch(fetchTransactions())
  }, [dispatch])

  useEffect(() => { setPage(1) }, [filter, search])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return list.filter((t) => {
      if (filter !== 'all' && t.status !== filter) return false
      if (!q) return true
      return (
        (t.txn_id || '').toLowerCase().includes(q) ||
        (t.user_name || '').toLowerCase().includes(q) ||
        (t.user_email || '').toLowerCase().includes(q) ||
        (t.order_ref || '').toLowerCase().includes(q) ||
        (t.gateway_ref || '').toLowerCase().includes(q)
      )
    })
  }, [list, filter, search])

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = visible.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const share = (n) => (summary.total ? Math.round((n / summary.total) * 100) : 0)

  const stats = [
    { label: 'Total Revenue', value: formatMoney(summary.total_revenue), icon: IndianRupee, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', trend: `${summary.total} txns`, variant: 'indigo' },
    { label: 'Successful', value: summary.success, icon: CheckCircle, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', trend: `${share(summary.success)}%`, variant: 'green' },
    { label: 'Pending', value: summary.pending, icon: Clock, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', trend: `${share(summary.pending)}%`, variant: 'amber' },
    { label: 'Failed', value: summary.failed, icon: XCircle, color: '#EF4444', colorGlow: 'rgba(239,68,68,0.15)', trend: `${share(summary.failed)}%`, variant: 'red' },
  ]

  const exportCsv = () => {
    const head = ['Transaction ID', 'User', 'Email', 'Order', 'Amount', 'Currency', 'Method', 'Status', 'Gateway Ref', 'Date']
    const rows = visible.map((t) => [
      t.txn_id, t.user_name, t.user_email, t.order_ref, t.amount, t.currency,
      t.method, t.status, t.gateway_ref, formatDate(t.paid_at || t.created_at),
    ])
    const csv = [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="dash-page page-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
        <h1 className="page-title">Transactions</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={() => dispatch(fetchTransactions())} disabled={isLoading}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-outline" onClick={exportCsv} disabled={!visible.length}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {isError && message && <div className="login-error">{message}</div>}

      <div className="stats-grid cols-4">
        {stats.map((s) => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{
              position: 'absolute', top: 0, right: 0, width: 110, height: 110,
              borderRadius: '50%', background: s.colorGlow, filter: 'blur(30px)',
              transform: 'translate(30%, -30%)', pointerEvents: 'none',
            }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color: s.color, background: `${s.color}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.trend}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg,${s.color}88,transparent)`,
              borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
            }} />
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
         
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
            <input
              className="input" style={{ paddingLeft: 32 }}
              placeholder="Search ID, user, email, order…"
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="view-tabs" style={{ margin: 0 }}>
            {['all', 'success', 'pending', 'failed', 'refunded'].map((f) => (
              <button key={f} className={`vtab${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
                {f === 'success' ? 'Paid' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th><th>User</th><th>Order</th><th>Amount</th>
                <th>Method</th><th>Status</th><th>Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>
              )}
              {!isLoading && visible.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)' }}>No transactions found</td></tr>
              )}
              {!isLoading && paginated.map((t) => {
                const st = STATUS[t.status] || STATUS.pending
                return (
                  <tr key={t.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{t.txn_id}</td>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text1)' }}>{t.user_name}</div>
                      {t.user_email && <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{t.user_email}</div>}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--text2)' }}>{t.order_ref || '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--color-green)' }}>{formatMoney(t.amount, t.currency)}</td>
                    <td style={{ color: 'var(--text2)' }}>{t.method || '—'}</td>
                    <td><span className={`badge ${st.cls}`}>{st.l}</span></td>
                    <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>{formatDate(t.paid_at || t.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {!isLoading && visible.length > 0 && (
          <Pagination
            total={visible.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      <style>{`
        .dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}
        .login-error{
          background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.35);
          color:#fca5a5; font-size:0.875rem; padding:0.6rem 0.9rem; border-radius:10px;
        }
      `}</style>
    </div>
  )
}